// Copied from cartadigital-api/src/assistant/assistant.contracts.ts @ 930fb26. Do not edit here.
// QueResto Action API contracts (plan §3.8): the request and response shapes
// of `restaurants/:restaurantId/assistant`. The admin copies this file
// verbatim as `src/interfaces/assistant.interface.ts`: change both together.

/** Carried by every proposal and action body; any other value → 426 ASSISTANT_UPDATE_REQUIRED. */
export const ASSISTANT_SCHEMA_VERSION = 1;

// ---- Ops ----

/** The proposal kinds (plan F3; 'stock.changes' since P5). */
export type TAssistantKind = 'menu.changes' | 'stock.changes';

export type TMenuOpName =
  | 'setAvailability'
  | 'setPrice'
  | 'adjustPrices'
  | 'renameItem'
  | 'setDescription'
  | 'createItem'
  | 'setDiscount' // P1
  | 'setPrices' // P1
  | 'createCategory' // P2
  | 'renameCategory' // P2
  | 'setCategoryDescription' // P2
  | 'setCategoryAvailability' // P2
  | 'setCategoryPosOnly' // P2
  | 'moveItems' // P2
  | 'reorderItems' // P2
  | 'reorderCategories' // P2
  | 'createOptionGroup' // P3
  | 'updateOptionGroup' // P3
  | 'linkOptionGroup' // P3
  | 'unlinkOptionGroup' // P3
  | 'reorderOptionGroups' // P3
  | 'addOptionChoices' // P3
  | 'updateOptionChoice' // P3
  | 'removeOptionChoices' // P3
  | 'reorderOptionChoices' // P3
  | 'addSizes' // P4
  | 'updateSize' // P4
  | 'removeSizes' // P4
  | 'reorderSizes' // P4
  | 'setItemSchedule' // P4
  | 'setCategorySchedule' // P4
  | 'duplicateItem' // P4
  | 'setPreparationNotes'; // P4

/** The ops of the `stock.changes` kind (P5). */
export type TStockOpName =
  | 'recordStock'
  | 'createStockItem'
  | 'updateStockItem'
  | 'setRecipe';

/** Every op name. */
export type TAssistantOpName = TMenuOpName | TStockOpName;

/** Who may ask for an op and on which plan: the admin UI's own gates (plan F1, F4). */
export interface IAssistantOpSpec {
  kind: TAssistantKind;
  /** toggle = CatalogDisable or CatalogManage; manage = CatalogManage; stock = CatalogManage in the proposal's branch. */
  permission: 'toggle' | 'manage' | 'stock';
  /** Lowest effective plan tier: 0 every plan, 1 Plus. */
  minTier: 0 | 1;
  /** Needs a role over every branch (besides `branchScope: 'all'`, checked for every op that has it). */
  allBranches: boolean;
}

/** One line per op, written literally; each phase adds its own lines. */
export const ASSISTANT_OP_SPECS: Readonly<Record<
  TAssistantOpName,
  IAssistantOpSpec
>> = {
  setAvailability: {
    kind: 'menu.changes',
    permission: 'toggle',
    minTier: 0,
    allBranches: false,
  },
  setPrice: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  adjustPrices: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 1,
    allBranches: false,
  },
  renameItem: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  setDescription: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  createItem: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  setDiscount: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  setPrices: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 1,
    allBranches: false,
  },
  createCategory: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  renameCategory: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  setCategoryDescription: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  setCategoryAvailability: {
    kind: 'menu.changes',
    permission: 'toggle',
    minTier: 0,
    allBranches: false,
  },
  setCategoryPosOnly: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  moveItems: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  reorderItems: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  reorderCategories: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  createOptionGroup: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  updateOptionGroup: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  linkOptionGroup: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  unlinkOptionGroup: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  reorderOptionGroups: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  addOptionChoices: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  updateOptionChoice: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  removeOptionChoices: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  reorderOptionChoices: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  addSizes: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  updateSize: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  removeSizes: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  reorderSizes: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  setItemSchedule: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 1,
    allBranches: false,
  },
  setCategorySchedule: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 1,
    allBranches: false,
  },
  duplicateItem: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: true,
  },
  setPreparationNotes: {
    kind: 'menu.changes',
    permission: 'manage',
    minTier: 0,
    allBranches: false,
  },
  recordStock: {
    kind: 'stock.changes',
    permission: 'stock',
    minTier: 1,
    allBranches: false,
  },
  createStockItem: {
    kind: 'stock.changes',
    permission: 'stock',
    minTier: 1,
    allBranches: false,
  },
  updateStockItem: {
    kind: 'stock.changes',
    permission: 'stock',
    minTier: 1,
    allBranches: false,
  },
  setRecipe: {
    kind: 'stock.changes',
    permission: 'stock',
    minTier: 1,
    allBranches: false,
  },
};

/** What a price change does to an existing discounted price (D2, plan F6). 'scale': adjustPrices only. */
export type TDiscountMode = 'keep' | 'remove' | 'scale';

/** Where reorderItems / reorderCategories put the listed ids (P2). */
export type TPlacement = 'first' | 'last';

/** createOptionGroup templates (P3): the Opciones editor's templates after its min 0 → null. */
export type TGroupTemplate =
  | 'chooseOne'
  | 'chooseMany'
  | 'pricedExtras'
  | 'removeIngredients';

/** The rules each template sets (P3; = admin domain/catalog/group-templates.ts). */
export const ASSISTANT_GROUP_TEMPLATES: Readonly<Record<
  TGroupTemplate,
  IGroupRulesValue
>> = {
  chooseOne: {
    type: 'choose',
    required: true,
    multiple: false,
    min: null,
    max: null,
  },
  chooseMany: {
    type: 'choose',
    required: false,
    multiple: true,
    min: null,
    max: 3,
  },
  pricedExtras: {
    type: 'add',
    required: false,
    multiple: true,
    min: null,
    max: 5,
  },
  removeIngredients: {
    type: 'choose',
    required: false,
    multiple: true,
    min: null,
    max: null,
  },
};

/** One choice of createOptionGroup / addOptionChoices (P3). */
export interface IChoiceInputDto {
  name: string; // ≤128
  price?: number; // the surcharge, 0..99999999.99
  discountedPrice?: number | null;
  description?: string; // ≤1000
  available?: boolean; // false = added paused (default true)
}

/** One variant of addSizes (P4). */
export interface ISizeInputDto {
  name: string; // ≤128
  price: number; // 0..99999999.99
  discountedPrice?: number | null; // absent vs null are told apart (D2 decision of a conversion)
}

/** One entry of `setPrices` (P1-2): `price` and/or `discountedPrice` (null removes the discount). */
export interface IPriceEntryDto {
  itemId: number;
  sizeId?: number;
  price?: number; // 0..99999999.99
  discountedPrice?: number | null; // 0..99999999.99; null removes it
}

/** One entry of `recordStock` (P5): purchase/loss = the amount moved, count = the new total. */
export interface IStockQuantityDto {
  stockItemId: number;
  quantity: number; // ≥0, 3 decimals
}

/** One line of `setRecipe` (P5): an existing stock item, or one createStockItem of the call creates (its ref). */
export interface IRecipeLineDto {
  stockItemId?: number;
  stockItemRef?: string; // a-z, 0-9, - or _ (≤16)
  quantity: number; // >0, 3 decimals
}

/**
 * One flat op of `POST proposals`, validated by the `AssistantOpDto` class
 * (dto/propose.dto.ts, §8.7); `toAssistantOps` checks per op: exactly one target,
 * `all` only for `adjustPrices`, `percent` xor `amount`.
 */
export interface IAssistantOpDto {
  op: TAssistantOpName;
  itemIds?: number[]; // ≤500
  categoryIds?: number[]; // ≤100
  all?: true;
  itemId?: number;
  sizeId?: number;
  categoryId?: number;
  available?: boolean;
  branchScope?: 'current' | 'all';
  price?: number; // 0..99999999.99
  percent?: number; // -90..500
  amount?: number; // -99999999.99..99999999.99
  roundTo?: number; // one of [0.05, 0.1, 0.5, 1, 5, 10, 50, 100, 500, 1000]
  name?: string; // ≤128
  description?: string; // ≤1000
  discountMode?: TDiscountMode; // P0: required when the target has a discounted price (D2)
  discountedPrice?: number | null; // P1 setDiscount: what customers pay; null removes it
  prices?: IPriceEntryDto[]; // P1 setPrices: 1..500 entries
  ref?: string; // P2 createCategory, P5 createStockItem: a-z, 0-9, - or _ (≤16); other ops of the call point at it (categoryRef, stockItemRef)
  categoryRef?: string; // P2 createItem / moveItems: the ref of a category created in this call
  menuId?: number | null; // P2 createCategory / reorderCategories: the menu (null = the base menu)
  posOnly?: boolean; // P2 setCategoryPosOnly
  placement?: TPlacement; // P2 reorderItems / reorderCategories: 'first' (default) or 'last'; P3 createOptionGroup / linkOptionGroup (default last) and reorderOptionGroups / reorderOptionChoices
  optionGroupId?: number; // P3
  optionGroupIds?: number[]; // P3 reorderOptionGroups: ≤100
  choiceId?: number; // P3 updateOptionChoice
  choiceIds?: number[]; // P3 removeOptionChoices / reorderOptionChoices: ≤100
  template?: TGroupTemplate; // P3 createOptionGroup
  required?: boolean; // P3
  multiple?: boolean; // P3 updateOptionGroup
  min?: number | null; // P3: 0..100, null = no minimum
  max?: number | null; // P3: 0..100, null = no maximum
  choices?: IChoiceInputDto[]; // P3: 1..50 per op
  copyOf?: number; // P3 createOptionGroup: the group to copy
  sizeIds?: number[]; // P4 removeSizes / reorderSizes: ≤20
  sizes?: ISizeInputDto[]; // P4 addSizes: 1..20
  hourRanges?: IHourRangeDto[] | null; // P4 setItemSchedule / setCategorySchedule: ≤50; null or [] = always
  notes?: string; // P4 setPreparationNotes: ≤2000, '' clears
  stockItemId?: number; // P5 updateStockItem
  movement?: 'purchase' | 'loss' | 'count'; // P5 recordStock
  items?: IStockQuantityDto[]; // P5 recordStock: 1..100
  note?: string; // P5 recordStock: ≤200
  unit?: TStockUnit; // P5 createStockItem / updateStockItem
  stockType?: 'ingredient' | 'product'; // P5 createStockItem (default ingredient)
  cost?: number; // P5: ≥0
  minimumQuantity?: number; // P5: ≥0, 3 decimals
  quantity?: number; // P5 createStockItem: the initial quantity, ≥0, 3 decimals
  lines?: IRecipeLineDto[]; // P5 setRecipe: 0..30 ([] = no recipe)
}

// ---- Views and results ----

// Every row-level type ships in Phase 0 (types only) so the admin's tolerant
// readers can be tested against rows of later phases; the tag names the phase
// that first emits each value.

export type TStockUnit = 'g' | 'kg' | 'ml' | 'l' | 'un' | 'pcs'; // P5 (= StockUnit)
export interface IHourRangeDto {
  day: number; // 0 = Sunday … 6
  from: string; // HH:MM
  to: string; // HH:MM
} // P4

export type TRowState = 'ok' | 'stale' | 'outlier' | 'new';
export type TRowReason =
  | 'PRICE_ZERO'
  | 'BIG_INCREASE'
  | 'BIG_DECREASE'
  | 'ROUNDING_STEP'
  | 'NAME_EXISTS'
  | 'CHANGED_SINCE'
  | 'GONE'
  | 'STOCK_NEGATIVE'; // P5
export type TRowEntity =
  | 'item'
  | 'size'
  | 'category'
  | 'menu'
  | 'group'
  | 'choice'
  | 'stockItem'
  | 'recipe';
export type TRowField =
  | 'price'
  | 'availability'
  | 'name'
  | 'description'
  | 'create' // v1
  | 'newCategory'
  | 'posOnly'
  | 'category'
  | 'order' // P2
  | 'groups'
  | 'newGroup'
  | 'rules'
  | 'newChoice'
  | 'remove' // P3
  | 'sizes'
  | 'newSize'
  | 'schedule'
  | 'copy'
  | 'notes' // P4
  | 'stockMove'
  | 'stockCount'
  | 'newStockItem'
  | 'unit'
  | 'cost'
  | 'minimum'
  | 'recipe'; // P5

// Values. Forward-compat rule (test T-COMPAT): a new shape either reuses a v1
// shape with the SAME meaning, or uses none of the v1 discriminating keys
// `price`, `available`, `text`, `name` + `categoryId`.
export interface IPriceValue {
  price: number;
  discountedPrice: number | null;
} // v1; also sizes and choices
export interface IAvailabilityValue {
  available: boolean;
  visible: boolean;
  hiddenBranchIds: number[] | null;
} // v1; also categories, sizes, choices (hiddenBranchIds null)
export interface ITextValue {
  text: string | null;
} // v1; also names, notes, unit codes
export interface ICreateValue {
  name: string;
  price: number;
  description: string | null;
  categoryId: number | null; // P2: null when categoryKey is set
  categoryKey?: string | null; // P2
  discountedPrice?: number | null; // P2 (absent = null)
  visible?: boolean; // P2 (absent = true)
}
export interface IFlagValue {
  on: boolean;
} // P2 posOnly
export interface IPlacementValue {
  categoryId: number | null;
  categoryKey: string | null;
  categoryName: string;
} // P2
export interface IOrderValue {
  ids: number[];
  names: string[]; // first 12
} // P2/P3/P4
export interface INewCategoryValue {
  name: string;
  description: string | null;
  menuId: number | null;
  visible: boolean;
} // P2
/** P3: a product's full link list. A group created in the same proposal has `id: null` and `groupKey` = its `newGroup:{n}` row key. */
export interface IGroupLinksValue {
  groups: Array<{
    id: number | null;
    groupKey: string | null;
    name: string;
    active: boolean;
  }>;
}
export interface IGroupRulesValue {
  type: 'choose' | 'add';
  required: boolean;
  multiple: boolean;
  min: number | null;
  max: number | null;
} // P3
/** P3: the group header and its choices only; its product links are `item:{id}:groups` rows. `copyOf` = the copied group (null = not a copy). */
export interface INewGroupValue {
  name: string;
  description: string | null;
  rules: IGroupRulesValue;
  copyOf: number | null;
  choices: Array<{
    name: string;
    price: number;
    discountedPrice: number | null;
    description: string | null;
    active: boolean;
  }>;
}
export interface INewChoiceValue {
  name: string;
  price: number;
  discountedPrice: number | null;
  description: string | null;
  active: boolean;
} // P3 choices, P4 sizes (always true)
export interface IRemovedValue {
  removed: true;
} // P3/P4
export interface ISizesValue {
  sizes: Array<{
    id: number | null;
    name: string;
    price: number;
    discountedPrice: number | null;
    active: boolean;
  }>;
} // P4
export interface IScheduleValue {
  hourRanges: IHourRangeDto[] | null; // null = always
} // P4
export interface ICopyValue {
  name: string;
  sourceItemId: number;
  sourceName: string;
} // P4
export interface IStockMoveValue {
  movement: 'purchase' | 'loss';
  delta: number;
  unit: TStockUnit;
} // P5
export interface IStockQuantityValue {
  quantity: number;
  unit: TStockUnit;
} // P5 count, minimum
export interface IMoneyValue {
  amount: number;
} // P5 cost
export interface INewStockItemValue {
  name: string;
  unit: TStockUnit;
  type: 'i' | 'p';
  cost: number;
  minimumQuantity: number;
  quantity: number;
} // P5
export interface IRecipeValue {
  lines: Array<{
    stockItemId: number | null;
    stockItemKey: string | null;
    name: string;
    quantity: number;
    unit: TStockUnit;
  }>;
} // P5
/** A value of a row (`from`, `to`, `seenFrom`, a stale row's `now`). */
export type TAssistantValue =
  | IPriceValue
  | IAvailabilityValue
  | ITextValue
  | ICreateValue
  | IFlagValue
  | IPlacementValue
  | IOrderValue
  | INewCategoryValue
  | IGroupLinksValue
  | IGroupRulesValue
  | INewGroupValue
  | INewChoiceValue
  | IRemovedValue
  | ISizesValue
  | IScheduleValue
  | ICopyValue
  | IStockMoveValue
  | IStockQuantityValue
  | IMoneyValue
  | INewStockItemValue
  | IRecipeValue;
export interface IAssistantRow {
  key: string; // grammar: plan F6, ≤64
  op: string;
  field: TRowField;
  /** Absent on rows stored before Phase 1: 'size' when sizeId is set, else 'item'. */
  entity?: TRowEntity;
  itemId: number | null;
  sizeId: number | null;
  /** The subject's display name (product, category, menu, group, stock item); never empty. */
  itemName: string;
  /** The second label part: size, choice, or (`groups` rows) nothing. */
  sizeName: string | null;
  categoryId: number | null; // P0 widening (null for groups, choices, menus, stock)
  categoryName: string | null; // P0 widening
  menuName: string | null;
  picture: string | null;
  menuId?: number | null; // P2: menu rows (0 never stored: the base menu is null)
  groupId?: number | null; // P3
  choiceId?: number | null; // P3
  stockItemId?: number | null; // P5
  /** P2: row keys that must be applied together with this row. Absent = none. */
  requires?: string[];
  from: TAssistantValue | null;
  to: TAssistantValue;
  state: TRowState;
  reasons: TRowReason[];
  seenFrom?: TAssistantValue | null;
}
export interface IAssistantExcludedRow {
  key?: string; // P0
  itemId: number | null; // P0 widening
  itemName: string;
  sizeName: string | null;
  reason:
    | 'NO_CHANGE'
    | 'DISCOUNT_INVALID'
    | 'PRICE_INVALID'
    | 'NO_BASE_PRICE'
    | 'HIDDEN_EVERYWHERE';
}
/** P3 (D3): the products that show a change of an existing or new option group: its live links plus this proposal's `item:{id}:groups` rows. */
export interface IAssistantAffectedGroup {
  groupId: number | null; // null = created by this proposal
  title: string;
  itemCount: number;
  items: Array<{ itemId: number; name: string }>; // ≤50, by name
  ownRules: number; // links with their own min/max/required/multiple
}
export interface IAssistantSummary {
  code: TAssistantOpName | 'mixed';
  count: number;
  available?: boolean;
  branchScope?: 'current' | 'all';
  percent?: number;
  amount?: number;
  roundTo?: number;
  categoryNames?: string[];
  targetAll?: boolean;
}
export type TAssistantProposalStatus =
  | 'proposed'
  | 'applied'
  | 'discarded'
  | 'superseded'
  | 'expired'
  | 'undone';
export interface IAssistantProposalView {
  id: string;
  kind: TAssistantKind;
  status: TAssistantProposalStatus;
  schemaVersion: 1;
  restaurantId: number;
  branchId: number;
  branchName: string;
  createdAt: string;
  expiresAt: string;
  viewHash: string;
  tainted: boolean;
  summary: IAssistantSummary;
  rows: IAssistantRow[];
  rowCount: number;
  excluded: IAssistantExcludedRow[];
  notes: Array<'TRANSLATIONS_UNCHANGED'>;
  affected?: IAssistantAffectedGroup[]; // P3 (D3): products that show a change of an option group
  requestText: string | null;
  supersededBy: string | null;
  result: IAssistantApplyResult | null;
}
export interface IAssistantApplyResult {
  proposalId: string;
  status: 'applied';
  appliedKeys: string[];
  appliedCount: number;
  appliedAt: string;
  undoUntil: string;
  itemIds: number[];
  createdItemIds: number[];
  categoryIds: number[];
  createdCategoryIds?: number[]; // P2
  optionGroupIds?: number[]; // P3: created and edited groups
  stockItemIds?: number[]; // P5: stock items moved, created or edited
  summary: IAssistantSummary;
  conversationAppliedCount: number; // applied (not undone) rows of this user in this conversation → history badge
  replayed?: true;
}
export interface IAssistantUndoResult {
  actionId: string;
  status: 'undone';
  reverted: number;
  kept: number;
  keptRows: Array<{ key: string; itemName: string }>;
  undoneAt: string;
  conversationAppliedCount: number;
  replayed?: true;
}
export interface IAssistantUserRef {
  id: number;
  name: string;
}
export interface IAssistantActivityItem {
  id: string;
  kind: TAssistantKind;
  status: 'applied' | 'undone';
  summary: IAssistantSummary;
  rowCount: number;
  appliedCount: number;
  preview: IAssistantRow[] /* first 3 */;
  user: IAssistantUserRef;
  createdAt: string;
  appliedAt: string;
  undoneAt: string | null;
  undoneBy: IAssistantUserRef | null;
  undoResult: IAssistantUndoResult | null;
  canUndo: boolean;
  undoUntil: string;
}
export interface IAssistantPhotoUpdate {
  sessionId: string;
  publishedAt: string;
  user: IAssistantUserRef | null;
  counts: {
    priceChanges: number;
    added: number;
    hidden: number;
    shown: number;
  };
  undone: boolean;
  canUndo: boolean;
}

// ---- Endpoints ----

/** GET config?branchId */
export interface IAssistantConfigResponse {
  schemaVersion: 1;
  writesEnabled: boolean;
  ops: string[];
  limits: {
    maxOps: number; // 20
    maxRows: number; // 500
    maxCreates: number; // 10
    proposalTtlMin: number; // 30
    undoDays: number; // 7
  };
  currency: { code: string; cents: boolean; roundSteps: number[] };
  supportUrl: string | null;
  /** P0: ops switched off for this user (ASSISTANT_DISABLED_OPS ∪ ASSISTANT_STAFF_ONLY_OPS unless staff). */
  disabledOps: string[];
  /** P0: host tools switched off for this user (ASSISTANT_DISABLED_TOOLS ∪ ASSISTANT_STAFF_ONLY_TOOLS unless staff). */
  disabledTools: string[];
}

/** POST identity-token */
export interface IAssistantIdentityTokenRequest {
  branchId: number;
  locale: string;
}
export interface IAssistantIdentityTokenResponse {
  token: string;
  expiresAt: string;
}

/** Where a proposal came from (the GPTHelp tool call). */
export interface IAssistantProposalSource {
  conversationId: string;
  turnId: string;
  requestId: string;
  tainted: boolean;
  requestText: string;
}
/** POST proposals */
export interface IAssistantProposeRequest {
  schemaVersion: 1;
  kind: TAssistantKind;
  branchId: number;
  ops: IAssistantOpDto[]; // 1..20
  source: IAssistantProposalSource;
}
export interface IAssistantProposeResponse {
  outcome: 'created' | 'merged' | 'empty';
  proposal: IAssistantProposalView | null;
  excluded: IAssistantExcludedRow[];
  supersededId: string | null;
}

/** POST proposals/:id/refresh, POST proposals/:id/discard and POST actions/:id/undo */
export interface IAssistantSchemaVersionRequest {
  schemaVersion: 1;
}
/** POST proposals/:id/apply */
export interface IAssistantApplyRequest {
  schemaVersion: 1;
  keys: string[];
  viewHash: string;
}
/** POST proposals/:id/discard */
export interface IAssistantDiscardResponse {
  status: TAssistantProposalStatus;
}

/** GET actions?before=<cursor>&limit=20; `before` is the opaque `nextCursor` of the previous page. */
export interface IAssistantActivityQuery {
  before?: string;
  limit?: number;
}
export interface IAssistantActivityResponse {
  items: IAssistantActivityItem[];
  photoUpdates: IAssistantPhotoUpdate[];
  nextCursor: string | null;
}

// ---- Errors: { statusCode, code, message: code, details } ----

export type TAssistantErrorCode =
  | 'ASSISTANT_WRITES_OFF' // 503: switch off or store not allowlisted
  | 'ASSISTANT_FORBIDDEN' // 403: not a member, wrong branch, missing permission (details.need)
  | 'ASSISTANT_PLAN_REQUIRED' // 403: adjustPrices below Plus (details.minTier)
  | 'ASSISTANT_ORIGIN' // 403: foreign Origin header
  | 'ASSISTANT_NOT_FOUND' // 404: unknown id, other store, other user's proposal
  | 'ASSISTANT_STALE' // 409: live value ≠ from, or viewHash mismatch
  | 'ASSISTANT_SUPERSEDED' // 409: reopening an expired proposal whose slot was taken
  | 'ASSISTANT_NOT_PENDING' // 409: wrong status for the operation (details.status; on apply of an undone action also details.undoResult)
  | 'ASSISTANT_UNDO_WINDOW' // 409: undo after 7 days
  | 'ASSISTANT_EXPIRED' // 410: apply after 30 min
  | 'ASSISTANT_INVALID_OP' // 422: shape errors, NOT_IN_STORE, HAS_SIZES, ROUND_STEP, KEYS, WRITE_REJECTED (details.reason)
  | 'ASSISTANT_TOO_MANY_ROWS' // 422: >500 rows, >20 ops, >10 creates
  | 'ASSISTANT_QUOTA' // 429: 200 proposals or 100 applies per store per rolling day
  | 'ASSISTANT_UPDATE_REQUIRED' // 426: unsupported schemaVersion
  | 'ASSISTANT_IDENTITY_OFF'; // 503: identity secret, KID or chatbot id not configured

/** `details` of 409 ASSISTANT_STALE. */
export interface IAssistantStaleDetails {
  proposal: IAssistantProposalView;
  changed: Array<{
    key: string;
    itemName: string;
    sizeName: string | null;
    now: TAssistantValue | null; // null: the row is gone
  }>;
}

/** `details` of 503 ASSISTANT_WRITES_OFF when one op is switched off (P0); absent when every write is off. */
export interface IAssistantWritesOffDetails {
  op: TAssistantOpName;
}

/** `details` of 403 ASSISTANT_FORBIDDEN (none for non-members and for refusals of the catalog writers). */
export interface IAssistantForbiddenDetails {
  op?: TAssistantOpName; // absent for the branch check
  need: 'catalogManage' | 'catalogToggle' | 'allBranches' | 'branch' | 'stock'; // stock (P5): CatalogManage in the proposal's branch
}

/** `details` of 403 ASSISTANT_PLAN_REQUIRED. */
export interface IAssistantPlanRequiredDetails {
  op: TAssistantOpName;
  minTier: number; // 1 = Plus
}

/** A price target whose discounted price needs the user's decision (D2, 422 DISCOUNT_DECISION). */
export interface IAssistantDiscountTarget {
  itemId: number | null;
  sizeId: number | null;
  choiceId: number | null;
  name: string; // «Pizza · Grande»
  price: number;
  discountedPrice: number;
  newPrice: number; // the regular price the op would set (adjustPrices: the computed one)
}

/** `details` of 409 ASSISTANT_NOT_PENDING. */
export interface IAssistantNotPendingDetails {
  status: TAssistantProposalStatus;
  undoResult?: IAssistantUndoResult; // apply of an undone action
}

/** `details.reason` of 422 ASSISTANT_INVALID_OP. */
export type TAssistantInvalidOpReason =
  // Shape errors found by `toAssistantOps`:
  | 'ONE_TARGET' // not exactly one of itemIds / categoryIds / all
  | 'ALL_NOT_ALLOWED' // `all` on an op other than adjustPrices
  | 'PERCENT_XOR_AMOUNT' // adjustPrices without exactly one of percent / amount
  | 'MISSING_FIELD' // a field the op needs is missing (details.field)
  // Found while rendering:
  | 'NOT_IN_STORE' // ids not live in the store (details.ids; P5: stock items not live in the proposal's branch, foreign groups or choices, sizes of another product)
  | 'HAS_SIZES' // setPrice without sizeId on a product with sizes (details.sizes)
  | 'ROUND_STEP' // roundTo not allowed for the currency (details.allowed)
  | 'DISCOUNT_DECISION' // P0: a price change of a discounted target without a decision (details.discounts)
  | 'SCALE_NOT_ALLOWED' // P0: discountMode 'scale' outside adjustPrices
  | 'DUPLICATE_TARGET' // P1: setPrices names one (itemId, sizeId) twice (details.ids = [its itemId]); P5: a stock item twice among the call's movements or in one recipe (details.ids = [the stock item]), or a stockItemRef twice in one recipe (details.ref)
  | 'REF_UNKNOWN' // P2: a categoryRef no createCategory of the call has; P5: a stockItemRef no createStockItem of the call has (details.ref)
  | 'REF_DUPLICATE' // P2: two createCategory ops of the call share a ref; P5: two createStockItem ops (details.ref)
  | 'OTHER_MENU' // P2: products or categories of another menu (details.ids)
  | 'NOT_IN_CATEGORY' // P2: ids that are not live members of the category or menu (details.ids)
  | 'ORDER_CONFLICT' // P2: a reorder of a list the same proposal also changes
  | 'TOO_LONG' // P2: a text longer than its column (details.field)
  | 'RULES_INVALID' // P3: the group's rules would not be valid (details.rule, details.message)
  | 'NO_ACTIVE_CHOICE' // P3: a required group would keep no active choice
  | 'SIZES_CONFLICT' // P4: a price row on a product whose sizes the proposal converts, or on a size it removes; a removeSizes that sends a one price (price, discountedPrice or discountMode) while the product ends with new variants, in either op order; at apply: an approved subset that mixes a product's conversion with its other size rows (details.key)
  | 'NO_ACTIVE_SIZE' // P4: a product would keep no active size (at apply: details.key)
  | 'SIZE_LIMIT' // P4: more than 20 live sizes (at apply: details.key)
  | 'SCHEDULE_INVALID' // P4: the hours would not be valid (details.message)
  | 'WRONG_KIND' // P5: an op of the other proposal kind
  // Found while applying:
  | 'KEYS' // an apply key that is not a row of the proposal
  | 'WRITE_REJECTED' // a catalog writer refused the change
  | 'NEEDS_ROW'; // P2: an approved row whose required row is not approved (details.key, details.needs)

/** `details` of 422 ASSISTANT_INVALID_OP. */
export interface IAssistantInvalidOpDetails {
  reason: TAssistantInvalidOpReason;
  opIndex?: number; // the offending op; absent for KEYS, WRITE_REJECTED and the refusals found while applying an approved subset (P2 NEEDS_ROW, P3 NO_ACTIVE_CHOICE / RULES_INVALID, P4 NO_ACTIVE_SIZE / SIZE_LIMIT / SIZES_CONFLICT), which carry key
  field?: keyof IAssistantOpDto; // MISSING_FIELD, TOO_LONG
  ids?: number[]; // NOT_IN_STORE: the missing ids (products, sizes; P3 groups, choices; P5 stock items); DUPLICATE_TARGET: [the repeated product or P5 stock item]
  sizes?: Array<{ id: number; title: string }>; // HAS_SIZES: the product's sizes
  allowed?: number[]; // ROUND_STEP: the steps allowed for the currency
  discounts?: IAssistantDiscountTarget[]; // DISCOUNT_DECISION: the first 20 undecided targets
  discountCount?: number; // DISCOUNT_DECISION: every undecided target (P1; discounts lists the first 20)
  ref?: string; // P2/P5 REF_UNKNOWN, REF_DUPLICATE; P5 DUPLICATE_TARGET (a stockItemRef twice in one recipe)
  key?: string; // P2 NEEDS_ROW: the approved row; at apply, P3 NO_ACTIVE_CHOICE / RULES_INVALID and P4 NO_ACTIVE_SIZE / SIZE_LIMIT / SIZES_CONFLICT: the first approved row of the group or product the approval breaks
  needs?: string; // P2 NEEDS_ROW: the row it requires
  rule?:
    | 'MIN_ABOVE_CHOICES'
    | 'MAX_ZERO'
    | 'MIN_ABOVE_MAX'
    | 'OWN_RULES_DIFFER'; // P3 RULES_INVALID (OWN_RULES_DIFFER: copyOf)
  message?: string; // P3 RULES_INVALID, P4 SCHEDULE_INVALID: a catalog validator's own text
}

/** `details` of 422 ASSISTANT_TOO_MANY_ROWS: the count that broke a limit, and that limit. */
export interface IAssistantTooManyRowsDetails {
  rows?: number; // max 500
  ops?: number; // max 20
  creates?: number; // max 10
  categories?: number; // P2: new categories, max 5
  groups?: number; // P3: new option groups, max 5
  choices?: number; // P3: new choices, max 100
  links?: number; // P3: product link rows, max 300
  products?: number; // P3: products of one option-group op, max 100
  itemGroups?: number; // P3: option groups one product would hold, max 100
  stockItems?: number; // P5: new stock items, max 20
  max: number;
}

/** `details` by error code; the codes not listed carry none. */
export interface IAssistantErrorDetails {
  ASSISTANT_WRITES_OFF: IAssistantWritesOffDetails;
  ASSISTANT_FORBIDDEN: IAssistantForbiddenDetails;
  ASSISTANT_PLAN_REQUIRED: IAssistantPlanRequiredDetails;
  ASSISTANT_STALE: IAssistantStaleDetails;
  ASSISTANT_NOT_PENDING: IAssistantNotPendingDetails;
  ASSISTANT_INVALID_OP: IAssistantInvalidOpDetails;
  ASSISTANT_TOO_MANY_ROWS: IAssistantTooManyRowsDetails;
}

/**
 * Error body of every assistant endpoint (the `apiError` util): a union over
 * the codes, so checking `code` types `details`.
 */
export type TAssistantErrorBody = {
  [C in TAssistantErrorCode]: {
    statusCode: number;
    code: C;
    message: C;
    details?: C extends keyof IAssistantErrorDetails
      ? IAssistantErrorDetails[C]
      : undefined;
  };
}[TAssistantErrorCode];
